import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ResultsTopic } from "@/types/results";
import { useTranslation } from "react-i18next";

export function ResultsTopicDialog({
  comparisonDateLabel,
  onOpenChange,
  open,
  topic,
}: {
  comparisonDateLabel: string | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  topic: ResultsTopic | null;
}) {
  const { t } = useTranslation();
  if (!topic) return null;

  return (
    <DialogPrimitive.Root onOpenChange={onOpenChange} open={open}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/65 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-48px)] w-[560px] max-w-[calc(100vw-48px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_24px_32px_rgba(13,31,20,0.30)] outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95">
          <header className="flex items-start gap-3.5 border-b border-[#dddfe4] px-6 pb-4 pt-6">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <DialogPrimitive.Title className="truncate text-xl font-semibold leading-[26px] text-[#0a0c11]">
                {topic.title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                {t("results.topic_dialog_description", { topic: topic.title })}
              </DialogPrimitive.Description>
              <span className="inline-flex h-6 shrink-0 items-center rounded-full bg-[#ebecf0] px-3 text-xs font-medium leading-[18px] tracking-[0.06px] text-[#5b616d]">
                {t("results.exam_weight", { weight: topic.examWeight })}
              </span>
            </div>
            <DialogPrimitive.Close
              aria-label={t("common.close")}
              className="rounded-md text-[#5b616d] outline-none hover:text-[#0a0c11] focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)]"
              type="button"
            >
              <X aria-hidden="true" className="h-6 w-6" />
            </DialogPrimitive.Close>
          </header>
          <div className="min-h-0 overflow-y-auto p-6">
            <section className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <strong className="text-[36px] font-semibold leading-[42px] tracking-[-0.18px] text-[#a8791c]">
                  {topic.score}<span className="text-xs leading-[18px]">%</span>
                </strong>
                <div className="text-sm font-medium leading-[21px]">
                  <p className="text-[#0a0c11]">{t("results.correct_summary", { correct: topic.correct, total: topic.total })}</p>
                  <p className="text-xs leading-[18px] tracking-[0.06px] text-[var(--dp-brand-button)]">
                    {topic.improvement == null
                      ? t("results.first_comparison_period")
                      : `${t(topic.improvement >= 0 ? "results.improvement" : "results.change", { percent: Math.abs(topic.improvement) })}${comparisonDateLabel ? ` ${t("results.since_date", { date: comparisonDateLabel })}` : ""}`}
                  </p>
                </div>
              </div>
              <div className="h-2 overflow-hidden rounded-[28px] bg-[#ebecf0]">
                <div className="h-full rounded-[28px] bg-[#a8791c]" style={{ width: `${topic.score}%` }} />
              </div>
            </section>
            <div className="mt-4 flex flex-col">
              {topic.subtopics.length > 0 ? topic.subtopics.map((subtopic, index) => (
                <div key={subtopic.id}>
                  <div className="flex items-center gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold leading-[21px] text-[#0a0c11]">{subtopic.title}</h3>
                      <p className="text-xs leading-[18px] tracking-[0.06px] text-[#5b616d]">
                        {t("results.correct_summary", { correct: subtopic.correct, total: subtopic.total })}
                      </p>
                    </div>
                    <span className="inline-flex h-6 items-center rounded-full bg-[#fdf6e7] px-2 text-xs font-semibold leading-[18px] tracking-[0.06px] text-[#a8791c]">
                      {subtopic.score}%
                    </span>
                  </div>
                  {index < topic.subtopics.length - 1 && <div className="h-px w-full bg-[#ebecf0]" />}
                </div>
              )) : (
                <p className="py-10 text-center text-sm text-[#5b616d]">{t("results.no_topic_data")}</p>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
